import { useEffect, useState } from "react";
import { BsPlusLg, BsXLg } from "react-icons/bs";
import { Select } from "@/common/components/selectInput";
import Input from "@/common/components/Input/Input";
import { H3 } from "@/common/elements";
import { KeyValueObj } from "@/common/types";
import { PackagingLevelsWrapper } from "./packaging-levels-style";
import useAxiosCall from "@/hooks/useAxiosCall";
import { PACKAGING_TYPES_URL } from "@/common/constants";

const MAX_LEVELS = 5;

interface ILevel {
  level: number;
  packagingType: string;
  quantity: string;
}

export interface IPackagingLevelsProps {
  onChange: (values: KeyValueObj, isValid: boolean) => void;
  editMode?: boolean;
  product?: KeyValueObj;
}

// Legacy fixed fields, biggest package first, to match the new Level 1 = biggest ordering.
const LEGACY_LEVEL_FIELDS = [
  { type: "tertiary_packaging", quantity: null },
  {
    type: "secondary_packaging",
    quantity: "secondary_package_quantity_per_tertiary_package",
  },
  {
    type: "primary_packaging",
    quantity: "primary_package_quantity_per_secondary_package",
  },
];

function buildInitialLevels(
  editMode?: boolean,
  product?: KeyValueObj,
): ILevel[] {
  if (editMode && product) {
    // Preferred: the new packaging_hierarchy array. Each entry's
    // containment_quantity describes how many of the NEXT (smaller) entry
    // it holds, so it becomes the quantity input on the following level.
    if (
      Array.isArray(product.packaging_hierarchy) &&
      product.packaging_hierarchy.length > 0
    ) {
      return product.packaging_hierarchy.map(
        (entry: KeyValueObj, i: number, arr: KeyValueObj[]) => {
          const prevEntry = arr[i - 1];
          return {
            level: i + 1,
            packagingType: entry.packaging_type || "",
            quantity:
              prevEntry?.containment_quantity !== undefined &&
              prevEntry?.containment_quantity !== null
                ? String(prevEntry.containment_quantity)
                : "",
          };
        },
      );
    }

    // Legacy fallback: fixed tertiary/secondary/primary fields.
    const existing = LEGACY_LEVEL_FIELDS.map(({ type, quantity }) => ({
      packagingType: product[type] || "",
      quantity:
        quantity && product[quantity] !== undefined && product[quantity] !== null
          ? String(product[quantity])
          : "",
    })).filter((lvl) => lvl.packagingType || lvl.quantity);

    if (existing.length > 0) {
      return existing.map((lvl, i) => ({ level: i + 1, ...lvl }));
    }
  }
  return [{ level: 1, packagingType: "", quantity: "" }];
}

// Level i's containment_quantity is the quantity entered on level i + 1
// (the "number of THIS packaging in one PARENT packaging" input).
function buildPackagingHierarchy(levels: ILevel[]): KeyValueObj[] {
  return levels.map((lvl, i) => {
    const entry: KeyValueObj = { packaging_type: lvl.packagingType };
    const nextLevel = levels[i + 1];
    if (nextLevel?.quantity) {
      entry.containment_quantity = Number(nextLevel.quantity);
    }
    return entry;
  });
}

export function PackagingLevels({
  onChange,
  editMode,
  product,
}: IPackagingLevelsProps) {
  const [levels, setLevels] = useState<ILevel[]>(() =>
    buildInitialLevels(editMode, product),
  );

  const [getPackagingTypes, packagingTypesData, , packagingTypesLoading] =
    useAxiosCall();

  const packagingTypes: KeyValueObj[] = (
    packagingTypesData?.packaging_types || []
  ).map((type: string) => ({ id: type, title: type }));

  useEffect(() => {
    getPackagingTypes({ url: PACKAGING_TYPES_URL, method: "GET" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLevels(buildInitialLevels(editMode, product));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, product]);

  useEffect(() => {
    const isValid = levels.every((lvl, i) => {
      if (i === 0) return !!lvl.packagingType;
      return lvl.packagingType && lvl.quantity && Number(lvl.quantity) > 0;
    });
    onChange({ packaging_hierarchy: buildPackagingHierarchy(levels) }, isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levels]);

  function handleTypeChange(level: number, title: string) {
    setLevels((prev) =>
      prev.map((lvl) =>
        lvl.level === level ? { ...lvl, packagingType: title } : lvl,
      ),
    );
  }

  function handleQuantityChange(level: number, value: string) {
    setLevels((prev) =>
      prev.map((lvl) => (lvl.level === level ? { ...lvl, quantity: value } : lvl)),
    );
  }

  function handleAddLevel() {
    setLevels((prev) =>
      prev.length >= MAX_LEVELS
        ? prev
        : [...prev, { level: prev.length + 1, packagingType: "", quantity: "" }],
    );
  }

  function handleRemoveLastLevel() {
    setLevels((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  }

  function getQuantityPrompt(index: number) {
    const currentType = levels[index]?.packagingType || "this package";
    const parentType = levels[index - 1]?.packagingType || "the previous package";
    return `Number of ${currentType}(s) in one ${parentType}`;
  }

  return (
    <PackagingLevelsWrapper>
      <div className="text-prompt">
        <H3>Product Packaging</H3>
        <p>
          Add each packaging level, starting with Level 1 as the biggest
          package and working down to the smallest.
        </p>
      </div>

      {levels.map((lvl, index) => (
        <div className="level-row" key={lvl.level}>
          <Select
            options={packagingTypes}
            readOnly={packagingTypesLoading}
            placeholder={
              packagingTypesLoading
                ? "Loading packaging types..."
                : `Select Level ${index + 1} packaging`
            }
            prompt={`Level ${index + 1} Packaging`}
            fullWidth={false}
            shortWidth={true}
            name={`packaging_level_${lvl.level}`}
            indexLevel={MAX_LEVELS - index}
            formValues={{ [`packaging_level_${lvl.level}`]: lvl.packagingType }}
            setFormValues={() => {}}
            required
            updateData={(_name, title) =>
              handleTypeChange(lvl.level, title as string)
            }
          />
          {index > 0 && (
            <Input
              inputType="number"
              prompt={getQuantityPrompt(index)}
              name={`packaging_level_${lvl.level}_quantity`}
              placeholder="Enter quantity"
              fullWidth={false}
              shortWidth={true}
              required
              formValues={{}}
              initialValue={lvl.quantity}
              rules={{
                aboveZero: {
                  expectedValue: true,
                  errorMessage: "Value must be more than zero",
                },
              }}
              updateData={(_name: string, value: string) =>
                handleQuantityChange(lvl.level, value)
              }
            />
          )}
          {index === levels.length - 1 && levels.length > 1 && (
            <button
              type="button"
              className="remove-level"
              onClick={handleRemoveLastLevel}
            >
              <BsXLg /> Remove level
            </button>
          )}
        </div>
      ))}

      <div className="add-level">
        {levels.length < MAX_LEVELS ? (
          <button type="button" onClick={handleAddLevel}>
            <BsPlusLg /> Add new level
          </button>
        ) : (
          <p className="max-reached">Maximum of {MAX_LEVELS} levels reached</p>
        )}
      </div>
    </PackagingLevelsWrapper>
  );
}
